class MainSearch extends HTMLElement {
  constructor() {
    super();
    this.injectStyles();
    this.input = this.querySelector('input[type="search"]');
    this.colorGroups = {
      schwarz: ['black', 'schwarz'], weiss: ['white', 'weiss', 'weiß'], grau: ['grey', 'gray', 'grau', 'anthracite'],
      blau: ['blue', 'blau', 'navy', 'avio', 'marine'], gruen: ['green', 'grün', 'gruen', 'olive', 'sage', 'khaki'],
      rot: ['red', 'rot', 'bordeaux', 'burgundy'], braun: ['brown', 'braun', 'cognac', 'tan'], beige: ['beige', 'sand', 'cream'],
      gelb: ['yellow', 'gelb'], orange: ['orange'], rosa: ['pink', 'rosa', 'rose'], lila: ['purple', 'lila', 'violet', 'violett']
    };
    this.setupForm();
    this.setupEvents();
    if (!this.handleShopifyZeroResults()) this.enhanceResults();
  }

  injectStyles() {
    if (document.getElementById('peakstyle-search-overrides')) return;
    const style = document.createElement('style');
    style.id = 'peakstyle-search-overrides';
    style.textContent = `
      .f-template-search>.f-template-search__header.container,.f-template-search .main-collection product-grid-container>.container{width:min(96vw,1720px)!important;max-width:1720px!important;padding-inline:clamp(1.5rem,2.5vw,4rem)!important}
      .f-template-search__search{width:100%!important;max-width:none!important}.f-template-search .main-collection__list{min-width:0;flex:1 1 auto}
      .search-fallback-banner,.search-zero-landing{width:min(96vw,1720px);margin:2rem auto;padding:clamp(2rem,4vw,4.5rem);background:rgba(var(--color-foreground),.035);border:1px solid var(--color-border)}
      .search-fallback-banner{display:flex;justify-content:space-between;align-items:center;gap:2rem;padding-block:1.8rem}.search-fallback-banner strong{display:block;margin-bottom:.4rem}.search-fallback-banner p{margin:0}
      .search-zero-landing{text-align:center}.search-zero-landing__eyebrow{text-transform:uppercase;letter-spacing:.08em;font-size:1.2rem;font-weight:700}.search-zero-landing h2{margin:1rem 0}.search-zero-landing__chips{display:flex;justify-content:center;flex-wrap:wrap;gap:.8rem;margin:2rem 0}.search-zero-landing__chips a{padding:.7rem 1.2rem;border:1px solid var(--color-border);border-radius:99px}.search-zero-landing__results{margin-top:3rem;text-align:left}.search-zero-landing__results .f-predictive-search__results-list{display:grid!important;grid-template-columns:repeat(4,minmax(0,1fr));gap:2rem}
      .search-predictive-relaxed{padding:1.2rem 1.5rem;margin:0 0 1.5rem;background:rgba(var(--color-foreground),.04)}.search-predictive-relaxed span{display:block;margin-top:.3rem}
      @media(max-width:990px){.search-zero-landing__results .f-predictive-search__results-list{grid-template-columns:repeat(2,minmax(0,1fr))}}
      @media(max-width:767.98px){.f-template-search>.f-template-search__header.container,.f-template-search .main-collection product-grid-container>.container{width:100%!important;padding-inline:1.5rem!important}.search-fallback-banner{display:block}.search-zero-landing__results .f-predictive-search__results-list{grid-template-columns:1fr}}
    `;
    document.head.appendChild(style);
  }

  normalizeText(value) { return String(value || '').toLocaleLowerCase('de-DE').replace(/ß/g, 'ss').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim(); }
  normalizeSize(value) {
    let text = this.normalizeText(value).replace(',', '.');
    if (!text) return '';
    text = text.replace(/(\d+)\s*½/g, (_, n) => String(Number(n) + .5)).replace(/(\d+)\s*⅓/g, (_, n) => String(Number(n) + 1 / 3)).replace(/(\d+)\s*⅔/g, (_, n) => String(Number(n) + 2 / 3)).replace(/(\d+)\s+1\s*\/\s*2\b/g, (_, n) => String(Number(n) + .5)).replace(/(\d+)\s+1\s*\/\s*3\b/g, (_, n) => String(Number(n) + 1 / 3)).replace(/(\d+)\s+2\s*\/\s*3\b/g, (_, n) => String(Number(n) + 2 / 3));
    const number = Number(text); return Number.isFinite(number) ? String(Math.round(number * 1000) / 1000) : text;
  }
  escapeRegExp(value) { return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
  escapeHtml(value) { const node = document.createElement('div'); node.textContent = value || ''; return node.innerHTML; }

  parseQuery(query) {
    const normalized = this.normalizeText(query);
    const sizePattern = '\\d{1,2}(?:\\s+(?:1\\s*\\/\\s*2|1\\s*\\/\\s*3|2\\s*\\/\\s*3)|[.,]\\d+|[⅓⅔½])?';
    const labeled = normalized.match(new RegExp(`\\b(?:grosse|groesse|gr\\.?|size)\\s*[:\\-]?\\s*(${sizePattern})(?=\\s|$)`, 'i'));
    const trailing = normalized.match(new RegExp(`(?:^|\\s)(${sizePattern})\\s*$`, 'i'));
    const rawSize = labeled ? labeled[1] : trailing ? trailing[1] : '';
    const size = this.normalizeSize(rawSize);
    let colorKey = '';
    const words = normalized.split(/\s+/);
    Object.keys(this.colorGroups).some((key) => {
      const aliases = [key, ...this.colorGroups[key]].map((v) => this.normalizeText(v));
      if (words.some((word) => aliases.includes(word))) { colorKey = key; return true; }
      return false;
    });
    let baseQuery = normalized.replace(new RegExp(`\\b(?:grosse|groesse|gr\\.?|size)\\s*[:\\-]?\\s*${sizePattern}(?=\\s|$)`, 'gi'), ' ').replace(/\b(?:farbe|grundfarbe|color)\s*[:\-]?\s*/gi, ' ');
    if (rawSize) baseQuery = baseQuery.replace(new RegExp(`(?:^|\\s)${this.escapeRegExp(rawSize)}\\s*$`, 'i'), ' ');
    if (colorKey) {
      [colorKey, ...this.colorGroups[colorKey]].map((v) => this.normalizeText(v)).forEach((alias) => { baseQuery = baseQuery.replace(new RegExp(`\\b${this.escapeRegExp(alias)}\\b`, 'gi'), ' '); });
      baseQuery += ` ${this.colorGroups[colorKey][0]}`;
    }
    return { original: query, baseQuery: baseQuery.replace(/\s+/g, ' ').trim(), size, rawSize, colorKey };
  }

  hidden(name, value) {
    if (!this.input?.form) return;
    let field = this.input.form.querySelector(`input[type="hidden"][name="${name}"]`);
    if (!field) { field = document.createElement('input'); field.type = 'hidden'; field.name = name; this.input.form.appendChild(field); }
    field.value = value;
  }

  setupForm() {
    if (!this.input?.form) return;
    if (this.input.id === 'f-search-drawer-input' && !this.closest('#Drawer-Search')) {
      this.input.id = 'f-search-page-input'; this.input.form.querySelector('label[for="f-search-drawer-input"]')?.setAttribute('for', this.input.id);
    }
    this.hidden('type', 'product'); this.hidden('options[prefix]', 'last'); this.hidden('options[unavailable_products]', 'hide');
    const original = new URLSearchParams(location.search).get('ps_original'); if (original) this.input.value = original;
  }

  setupEvents() {
    this.input?.form?.addEventListener('submit', () => {
      const parsed = this.parseQuery(this.input.value);
      this.hidden('ps_original', parsed.original); this.hidden('ps_size', parsed.size); this.hidden('ps_color', parsed.colorKey);
      if (parsed.baseQuery) this.input.value = parsed.baseQuery;
    }, { capture: true });
  }

  criteria() {
    const params = new URLSearchParams(location.search);
    const original = params.get('ps_original') || params.get('q') || this.input?.value || '';
    const parsed = this.parseQuery(original);
    return { original, baseQuery: parsed.baseQuery || params.get('q') || '', size: this.normalizeSize(params.get('ps_size') || parsed.size), rawSize: parsed.rawSize, colorKey: params.get('ps_color') || parsed.colorKey };
  }

  handleShopifyZeroResults() {
    const params = new URLSearchParams(location.search);
    const query = params.get('q') || '';
    if (!query) return false;
    const grid = document.querySelector('[data-products-grid]');
    const cards = grid ? [...grid.querySelectorAll(':scope > .f-column')] : [];
    if (cards.length) return false;
    const original = params.get('ps_original') || query;
    const parsed = this.parseQuery(original);
    if (parsed.baseQuery && this.normalizeText(parsed.baseQuery) !== this.normalizeText(query) && !params.get('ps_fallback')) {
      params.set('q', parsed.baseQuery); params.set('type', 'product'); params.set('options[prefix]', 'last'); params.set('options[unavailable_products]', 'hide'); params.set('ps_original', original); params.set('ps_fallback', '1');
      if (parsed.size) params.set('ps_size', parsed.size); if (parsed.colorKey) params.set('ps_color', parsed.colorKey);
      location.replace(`${location.pathname}?${params}`); return true;
    }
    this.renderZeroLanding(original, parsed); return true;
  }

  suggestions(base) {
    const words = this.normalizeText(base).split(' ').filter(Boolean), output = [];
    if (base) output.push(base);
    if (words.length > 2) output.push(words.slice(0, -1).join(' '));
    if (words.length > 2) output.push(words.slice(1).join(' '));
    if (words.length > 1) output.push(words.slice(0, 2).join(' '));
    return [...new Set(output.filter((v) => v && v.length > 1))].slice(0, 4);
  }

  searchUrl(query, original) {
    const params = new URLSearchParams({ q: query, type: 'product', 'options[prefix]': 'last', 'options[unavailable_products]': 'hide', ps_original: original, ps_fallback: '1' });
    return `${location.pathname}?${params}`;
  }

  renderZeroLanding(original, parsed) {
    this.querySelector('.f-template-search__empty, .f-template-search__no-results, [data-search-empty]')?.setAttribute('hidden', '');
    const terms = this.suggestions(parsed.baseQuery || original);
    const section = document.createElement('section'); section.className = 'search-zero-landing';
    section.innerHTML = `<span class="search-zero-landing__eyebrow">Suche verfeinern</span><h2>Keine exakten Treffer für „${this.escapeHtml(original)}“</h2><p>Wir suchen automatisch nach ähnlichen Produktnamen und weniger spezifischen Begriffen.</p><div class="search-zero-landing__chips">${terms.map((term) => `<a href="${this.searchUrl(term, original)}">${this.escapeHtml(term)}</a>`).join('')}</div><div class="search-zero-landing__status" aria-live="polite">Naheliegende Treffer werden geladen …</div><div class="search-zero-landing__results"></div>`;
    this.appendChild(section); this.loadNearby(terms, section);
  }

  async loadNearby(terms, section) {
    const status = section.querySelector('.search-zero-landing__status'), target = section.querySelector('.search-zero-landing__results');
    for (const term of terms) {
      try {
        const params = new URLSearchParams({ q: term, section_id: 'predictive-search', 'resources[type]': 'product', 'resources[limit]': '8', 'resources[options][unavailable_products]': 'hide' });
        const response = await fetch(`${window.FoxThemeSettings.routes.predictive_search_url}?${params}`, { headers: { Accept: 'text/html' } });
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const list = doc.querySelector('.f-predictive-search__result-products, .f-predictive-search__block-products');
        if (list && list.querySelector('[role="option"], li')) { target.innerHTML = list.outerHTML; status.textContent = `Ähnliche Treffer für „${term}“`; return; }
      } catch (error) { console.warn('Search fallback failed', error); }
    }
    status.textContent = 'Keine nahen Produkttreffer gefunden. Nutze einen Suchvorschlag oder entferne weitere Begriffe.';
  }

  variantMatches(title, criteria) {
    const parts = this.normalizeText(title).split('/').map((v) => v.trim()), color = parts[0] || '', size = parts.at(-1) || '';
    if (criteria.size && this.normalizeSize(size) !== criteria.size) return false;
    if (criteria.colorKey) {
      const aliases = [criteria.colorKey, ...(this.colorGroups[criteria.colorKey] || [])].map((v) => this.normalizeText(v));
      if (!aliases.some((alias) => color.includes(alias))) return false;
    }
    return true;
  }

  async enhanceResults() {
    const grid = document.querySelector('[data-products-grid]'), criteria = this.criteria();
    if (!grid) return;
    const cards = [...grid.querySelectorAll(':scope > .f-column')]; if (!cards.length) return;
    if (!criteria.size && !criteria.colorKey) { if (new URLSearchParams(location.search).get('ps_fallback')) this.showFallbackBanner(criteria, false); return; }
    let visible = 0; const sizes = new Set(), colors = new Set(), states = [];
    await Promise.all(cards.map(async (column) => {
      const card = column.querySelector('.product-card'), link = card?.querySelector('.product-card__link, .full-width-link'); if (!link) return;
      try {
        const url = new URL(link.href), response = await fetch(`${url.pathname}.js`, { headers: { Accept: 'application/json' } }), product = await response.json();
        const variants = (product.variants || []).filter((variant) => variant.available);
        variants.forEach((variant) => { const parts = this.normalizeText(variant.title).split('/').map((v) => v.trim()); if (parts.length > 1) { sizes.add(parts.at(-1)); colors.add(parts[0]); } });
        const match = variants.find((variant) => this.variantMatches(variant.title, criteria)); states.push({ column, card, path: url.pathname, match });
        if (match) { visible += 1; this.applyVariant(card, url.pathname, match, criteria); } else column.hidden = true;
      } catch (error) { states.push({ column, card, match: null }); column.hidden = true; }
    }));
    if (!visible) { states.forEach((state) => { state.column.hidden = false; }); this.showRelaxedResults(grid, criteria, cards.length, sizes, colors); visible = cards.length; }
    const count = document.querySelector('[data-product-count]'); if (count) count.textContent = `${visible} passende${visible === 1 ? 's' : ''} Produkt${visible === 1 ? '' : 'e'}`;
  }

  applyVariant(card, path, variant, criteria) {
    const variantUrl = `${path}?variant=${variant.id}`;
    card.querySelectorAll('a[href]').forEach((link) => { if (new URL(link.href).pathname === path) link.href = variantUrl; });
    const info = card.querySelector('.product-card__info');
    if (info && !info.querySelector('[data-search-variant-match]')) { const note = document.createElement('div'); note.className = 'product-card__search-match'; note.dataset.searchVariantMatch = ''; note.textContent = [criteria.size && `Größe ${this.displaySize(criteria.size)}`, criteria.colorKey && `Grundfarbe ${criteria.colorKey}`].filter(Boolean).join(' · ') + ' verfügbar'; info.appendChild(note); }
  }

  showRelaxedResults(grid, criteria, count, sizes, colors) {
    this.showFallbackBanner(criteria, true);
    const panel = document.createElement('div'); panel.className = 'search-criteria-panel';
    panel.innerHTML = `<div><span class="search-criteria-panel__eyebrow">Ähnliche Treffer</span><div class="search-criteria-panel__chips">${criteria.size ? `<span>Gesuchte Größe ${this.displaySize(criteria.size)}</span>` : ''}${criteria.colorKey ? `<span>Gesuchte Farbe ${criteria.colorKey}</span>` : ''}</div></div><p class="search-criteria-panel__status">Keine exakte verfügbare Variante gefunden. ${count} passende Produkte zum Modellbegriff werden angezeigt.</p><div class="search-criteria-panel__alternatives">${sizes.size ? `<p><strong>Verfügbare Größen:</strong> ${[...sizes].slice(0, 10).join(', ')}</p>` : ''}${colors.size ? `<p><strong>Verfügbare Farben:</strong> ${[...colors].slice(0, 8).join(', ')}</p>` : ''}</div>`;
    grid.before(panel);
  }

  showFallbackBanner(criteria, variantMiss) {
    if (document.querySelector('.search-fallback-banner')) return;
    const banner = document.createElement('div'); banner.className = 'search-fallback-banner';
    banner.innerHTML = `<div><strong>Keine exakten Treffer für „${this.escapeHtml(criteria.original)}“.</strong><p>Wir zeigen ${variantMiss ? 'naheliegende Produkte' : 'Ergebnisse'} für „${this.escapeHtml(criteria.baseQuery)}“${criteria.size ? `; Größe ${this.displaySize(criteria.size)} wurde separat geprüft` : ''}.</p></div>`;
    this.querySelector('.f-template-search__header')?.after(banner);
  }

  displaySize(size) {
    const number = Number(size); if (!Number.isFinite(number)) return size;
    const whole = Math.floor(number), fraction = number - whole;
    if (Math.abs(fraction - .5) < .01) return `${whole} 1/2`;
    if (Math.abs(fraction - 1 / 3) < .01) return `${whole} 1/3`;
    if (Math.abs(fraction - 2 / 3) < .01) return `${whole} 2/3`;
    return String(number).replace('.', ',');
  }
}
customElements.define('main-search', MainSearch);
